import { faFlagCheckered } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ExclamationTriangleIcon } from "@heroicons/react/16/solid";
import { ChatBubbleOvalLeftEllipsisIcon } from "@heroicons/react/24/outline";
import { useEffect } from "react";
import { Handle, Position } from "reactflow";
import { WhatsappTemplateCategoryEnum } from "../enums";
import LoadingText from "../externalComponents/LoadingText";
import { Badge } from "../externalComponents/tailwind/badge";
import { Text } from "../externalComponents/tailwind/text";
import { LimitedTimeOfferPreview } from "../externalComponents/TemplatePreview/LimitedTimeOfferPreview";
import { formatParagraph } from "../functions/formatParagraph";

const handleIds = [..."abcdefghijklmnopqrstuvwxyz"];
const handleUrlIds = ["urlA", "urlB"];
const buttonHeight = 40;
const buttonMargin = 10;

function TemplateNode({ id, data, selected }) {
  useEffect(() => {
    const videoElements = document.querySelectorAll(
      `[id^="video-template-${id}-"]`,
    );
    videoElements.forEach((videoElement) => {
      videoElement.load();
    });
  }, [data.headerParameters?.video?.link, data.cards]);

  return (
    <div
      className={[
        data.hasFailed ? "failed relative" : "",
        data.isEditing ? "editing relative" : "",
        data.isPending ? "pending relative" : "",
        selected ? "selected" : "",
        data.isWaMeLink ? "wa-me-link" : "",
        "template-node",
      ]
        .join(" ")
        .trim()}
      onClick={() => data.clickNode(id)}
    >
      {data.isTemplate ? null : (
        <Handle type="target" position={Position.Left} />
      )}
      {/* {data.isRoot ? null : <Handle type="target" position={Position.Left} />} */}
      <div className="h-[30px] border rounded-[10px] flex justify-between items-center">
        <div className="ml-1 flex">
          {data.isTemplate && (
            <FontAwesomeIcon
              icon={faFlagCheckered}
              className="h-[20px] top-2 left-2"
            />
          )}
        </div>
        <div className="mr-1 flex">
          {data.category ? (
            <>
              {data.category &&
                !data.isEditing &&
                !data.isPending &&
                data.whatsappTemplateName !== "" && (
                  <Badge
                    className="h-[25px] top-2 right-2 mr-1"
                    color={
                      ([
                        WhatsappTemplateCategoryEnum.MARKETING,
                        WhatsappTemplateCategoryEnum.LIMITED_TIME_OFFER,
                        WhatsappTemplateCategoryEnum.CAROUSEL,
                      ].includes(data.category) &&
                        "sky") ||
                      (data.category === WhatsappTemplateCategoryEnum.UTILITY &&
                        "amber") ||
                      "violet"
                    }
                  >
                    {(
                      ([
                        WhatsappTemplateCategoryEnum.CAROUSEL,
                        WhatsappTemplateCategoryEnum.LIMITED_TIME_OFFER,
                      ].includes(data.category) &&
                        WhatsappTemplateCategoryEnum.MARKETING) ||
                      data.category
                    )?.toLowerCase()}
                  </Badge>
                )}
            </>
          ) : data.isEditing || data.whatsappTemplateName === "" ? null : (
            <span className="w-[80px]">
              <LoadingText />
            </span>
          )}
          {(data.hasFailed || data.status === "rejected") && !data.isPending ? (
            <>
              {data.status === "rejected" ? (
                <Badge color="rose" className="h-[25px] top-2 right-2">
                  {data.status}
                </Badge>
              ) : (
                <ExclamationTriangleIcon className="h-[25px] text-red-500 top-2 right-2" />
              )}
            </>
          ) : data.isEditing && !data.isPending ? (
            <Badge color="violet" className="h-[25px] top-2 right-2">
              not submitted yet
            </Badge>
          ) : data.isPending ? (
            <Badge color="orange" className="h-[25px] top-2 right-2">
              pending
            </Badge>
          ) : !data.hasFailed && !data.isEditing && data.status ? (
            <Badge color="lime" className="h-[25px] top-2 right-2">
              {data.status}
            </Badge>
          ) : null}
        </div>
      </div>
      {data.headerParameters?.image && (
        <>
          <img
            alt={`Image`}
            width="100%"
            style={{
              maxHeight: "400px",
              objectFit: "cover",
              objectPosition: "center",
              borderRadius: "10px 10px 0 0",
            }}
            src={
              data.headerParameters?.image?.link !== ""
                ? data.headerParameters?.image === "shopifyProductImage"
                  ? "https://storage.gadget.dev/files/22129/42940/media/file/O68TikY4GnkGUAzFNnGnB/c9168318-968c-4199-80dc-64da075a0aba"
                  : data.headerParameters.image.link
                : "https://firebasestorage.googleapis.com/v0/b/phocea-75b47.appspot.com/o/Compa%2Fph_image.png?alt=media&token=b42e2452-09c4-4076-9956-15520363da4b"
            }
          />
        </>
      )}
      {data.headerParameters?.video && (
        <>
          {data.headerParameters?.video?.link !== "" ? (
            <video
              id={`video-template-${id}`}
              key={data?.headerParameters?.video?.link}
              width="100%"
              style={{
                maxHeight: "400px",
                objectFit: "cover",
                objectPosition: "center",
                borderRadius: "10px 10px 0 0",
              }}
            >
              <source
                src={data.headerParameters?.video?.link}
                type="video/mp4"
              />
            </video>
          ) : (
            <img
              alt="Video"
              width="100%"
              style={{
                maxHeight: "400px",
                objectFit: "cover",
                objectPosition: "center",
                borderRadius: "10px 10px 0 0",
              }}
              src="https://firebasestorage.googleapis.com/v0/b/phocea-75b47.appspot.com/o/Compa%2Fph_video.png?alt=media&token=33b76a14-ff16-41b0-acfb-48b1159ba993"
            />
          )}
        </>
      )}
      {data.limitedTimeOffer && (
        <div className="mb-2">
          <LimitedTimeOfferPreview
            headerMedia={data.headerParameters}
            limitedTimeOffer={data.limitedTimeOffer}
            copyCodeButton={data.copyCodeButton}
            isInPanel={true}
          />
        </div>
      )}

      <div className="row">
        {data.body ||
        data.headerParameters ||
        data.urlButton?.text ||
        data.urlButton?.url ||
        data.cards ? (
          <div className="p-2">
            {data.body && (
              <>
                {formatParagraph(data.body?.trim()).map(
                  (formattedLine, index) => (
                    <Text className="text-xs" as="div" key={index}>
                      {formattedLine}
                    </Text>
                  ),
                )}
                {data.footer && (
                  <Text className="text-xxs text-gray-500" as="div">
                    {data.footer}
                  </Text>
                )}
              </>
            )}
            {data.cards && (
              <div className="w-full flex flex-col items-center">
                {data.cards?.map((card, cardIndex) => {
                  const header = card.components.find(
                    (c) => c.type === "HEADER",
                  );
                  const body = card.components.find((c) => c.type === "BODY");
                  const buttons = card.components.find(
                    (c) => c.type === "BUTTONS",
                  );

                  return (
                    <div
                      key={`header-${cardIndex}-${data.id}`}
                      className="w-[95%] flex flex-col mb-2 border rounded-customBorderRadius"
                    >
                      {header?.format === "IMAGE" && (
                        <img
                          alt={`Image`}
                          width="100%"
                          style={{
                            height: "150px",
                            objectFit: "cover",
                            objectPosition: "center 32%",
                            borderRadius: "10px 10px 10px 10px",
                          }}
                          src={
                            header?.link !== ""
                              ? header.link
                              : "https://firebasestorage.googleapis.com/v0/b/phocea-75b47.appspot.com/o/Compa%2Fph_image.png?alt=media&token=b42e2452-09c4-4076-9956-15520363da4b"
                          }
                        />
                      )}

                      {header?.format === "VIDEO" && (
                        <>
                          {header?.link !== "" ? (
                            <video
                              id={`video-template-${id}-${cardIndex}`}
                              width="100%"
                              style={{
                                height: "150px",
                                objectFit: "cover",
                                objectPosition: "center 32%",
                                borderRadius: "10px 10px 10px 10px",
                              }}
                            >
                              <source src={header?.link} type="video/mp4" />
                            </video>
                          ) : (
                            <img
                              alt="Video"
                              width="100%"
                              style={{
                                maxHeight: "150px",
                                objectFit: "cover",
                                objectPosition: "center 32%",
                                borderRadius: "10px 10px 10px 10px",
                              }}
                              src="https://firebasestorage.googleapis.com/v0/b/phocea-75b47.appspot.com/o/Compa%2Fph_video.png?alt=media&token=33b76a14-ff16-41b0-acfb-48b1159ba993"
                            />
                          )}
                        </>
                      )}
                      {body && (
                        <div>
                          <Text className="text-xs p-1 align-left">
                            {body.text}
                          </Text>
                        </div>
                      )}
                      {buttons && (
                        <div>
                          {buttons.buttons.map((b, buttonIndex) => {
                            const id = handleIds[cardIndex * 2 + buttonIndex];
                            const isUrlButton =
                              b?.type === "URL" || b?.type === "url";
                            const isQuickReply =
                              b?.type === "QUICK_REPLY" ||
                              b?.type === "quick_reply";
                            const isConnectable = !(
                              isUrlButton && b?.url?.includes("{{1}}")
                            );

                            return (
                              <div
                                key={buttonIndex}
                                className={`flex justify-center ${
                                  isUrlButton ? "urlButton-card" : "button-card"
                                }`}
                              >
                                <Text className="text-xs w-full text-center">
                                  {b?.text}
                                </Text>

                                {isUrlButton &&
                                  b?.urlClicks &&
                                  b?.trackingEnabled !== false && (
                                    <div className="clicksBadge group">
                                      <Badge color="sky">
                                        <Text className="text-xs hidden group-hover:block">
                                          ({b?.urlClicks})
                                        </Text>
                                        <Text className="text-xs">
                                          {Math.min(
                                            100,
                                            (b?.urlClickRate * 100).toFixed(1),
                                          )}
                                          %
                                        </Text>
                                      </Badge>
                                    </div>
                                  )}

                                {isQuickReply && b?.clickRate > 0 && (
                                  <div className="clicksBadge group">
                                    <Badge color="sky">
                                      <Text className="text-xs hidden group-hover:block">
                                        ({b?.clicks})
                                      </Text>
                                      <Text className="text-xs">
                                        {Math.min(
                                          100,
                                          (b?.clickRate * 100).toFixed(1),
                                        )}
                                        %
                                      </Text>
                                    </Badge>
                                  </div>
                                )}
                                <div className="">
                                  <Handle
                                    type="source"
                                    id={id}
                                    className={
                                      isConnectable
                                        ? "relative"
                                        : " relative not-connectable"
                                    }
                                    isConnectable={isConnectable}
                                    position={Position.Right}
                                    style={{
                                      top: "auto",
                                      right: -20,
                                      bottom: -10,
                                    }}
                                  />
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ) : (
          <div className="flex items-center justify-center h-[48px]">
            <ChatBubbleOvalLeftEllipsisIcon className="h-[30px] mr-3" />
            <Text>Choose a template</Text>
          </div>
        )}
      </div>

      {data.copyCodeButton?.text && (
        <div className="copy-code-button">
          {/* TODO Change this DOM to keep css instead of empty div */}
          <div></div>
          <Text className="text-xs">🗗 Copy offer code</Text>
          <div></div>
        </div>
      )}

      {data.urlButton && (
        <div className="urlButton">
          <div />
          <Text className="text-xs">🔗 {data.urlButton.text}</Text>
          {data.urlClicks && data.urlButton?.trackingEnabled !== false ? (
            <div className="clicksBadge group">
              <Badge color="sky">
                <Text className="text-xs hidden group-hover:block">
                  ({data.urlClicks})
                </Text>
                <Text className="text-xs">
                  {Math.min(100, (data.urlClickRate * 100).toFixed(1))}%
                </Text>
              </Badge>
            </div>
          ) : (
            <div />
          )}
          <Handle
            type="source"
            id={handleUrlIds[0]}
            className={`${
              data.urlButton?.url?.includes("{{1}}") ? "not-connectable" : ""
            }`}
            isConnectable={!data.urlButton?.url?.includes("{{1}}")}
            position={Position.Right}
            style={{
              top: "auto",
              bottom:
                data.buttons.length * buttonHeight +
                buttonMargin +
                (data.urlButton2 ? buttonHeight : 0),
              right: 5,
            }}
          />
        </div>
      )}
      {data.urlButton2 && (
        <div className="urlButton">
          <div />
          <Text className="text-xs">🔗 {data.urlButton2.text}</Text>
          {data.urlClicks2 && data.urlButton2?.trackingEnabled !== false ? (
            <div className="clicksBadge group">
              <Badge color="sky">
                <Text className="text-xs hidden group-hover:block">
                  ({data.urlClicks2})
                </Text>
                <Text className="text-xs">
                  {Math.min(100, (data.urlClickRate2 * 100).toFixed(1))}%
                </Text>
              </Badge>
            </div>
          ) : (
            <div />
          )}
          <Handle
            type="source"
            id={handleUrlIds[1]}
            className={`${
              data.urlButton2?.url?.includes("{{1}}") ? "not-connectable" : ""
            }`}
            isConnectable={!data.urlButton2?.url?.includes("{{1}}")}
            position={Position.Right}
            style={{
              top: "auto",
              bottom: data.buttons.length * buttonHeight + buttonMargin,
              right: 5,
            }}
          />
        </div>
      )}
      {data.buttons?.map((button, index) => (
        <div className="button" key={index}>
          <div />
          <Text className="text-xs">{button?.title}</Text>
          {button?.clicks ? (
            <div className="clicksBadge group">
              <Badge color="sky">
                <Text className="text-xs hidden group-hover:block">
                  ({button.clicks})
                </Text>
                <Text className="text-xs">
                  {Math.min(100, (button?.clickRate * 100).toFixed(1))}%
                </Text>
              </Badge>
            </div>
          ) : (
            <div />
          )}
          <Handle
            type="source"
            id={handleIds[index]}
            position={Position.Right}
            style={{
              top: "auto",
              bottom:
                (data.buttons.length - index - 1) * buttonHeight + buttonMargin,
              right: 5,
            }}
          />
        </div>
      ))}

      <Handle
        type="source"
        id="main"
        position={Position.Right}
        style={{
          bottom: "auto",
          top: 50,
        }}
      />
    </div>
  );
}

export default TemplateNode;
